// Admin "Enter as user" (impersonation) support session.
//
// ArcadeBrain's Hub admin panel mints a one-time code and opens the app at
// `<appUrl>?impersonate=<code>&impersonateServer=<origin>` in a fresh tab. This module redeems that
// code into a REAL session for the target user, so the operator sees exactly what that person sees.
//
// Why the cookie flavour (`/web/impersonate-exchange`) and not the JWT one (`/auth/v1/...`):
// this app talks to `/web/*` for almost everything, and every one of those endpoints authenticates
// from the session cookie and nothing else. A JWT session there fails in the worst possible way —
// the handover succeeds, the banner appears, and then the first background poll (`web/ping`) 403s
// and signs the operator straight back out. ArcadeBrain exposes both flavours of the same handover;
// the code is identical, only the endpoint that redeems it differs.
//
// Because the result is an ordinary session, the rest of the app needs no special case at all: the
// normal `checkAuth()` bootstrap, run-process, SSE and uploads all work unmodified. The only things
// this module owns afterwards are the banner and Exit.
//
// NOTE the consequence: a session cookie is browser-wide, so entering an account replaces the
// operator's own session for this app in that browser. That is how "Login as super user" has always
// behaved; the tab-scoping the earlier JWT version had was a property of the token, not of a choice.

import { detectBrand } from "./brand";
import api, { getBaseURL } from "../services/api";

const CODE_QUERY_KEY = "impersonate";
const SERVER_QUERY_KEY = "impersonateServer";
const SESSION_KEY = "impersonationSession";
const IMPERSONATOR_KEY = "impersonator";

// Hosts a handover may name as its backend. The admin console tells us which ArcadeBrain node minted
// the code (the code lives in that node's memory only, and one frontend host fronts several
// backends), but that value arrives in a URL anyone can craft — and whatever it names becomes the
// origin this tab exchanges against AND then sends every subsequent API call to. An unchecked value
// would let a hostile link run the app against an attacker's server inside a trusted origin, so the
// host is matched by parsed hostname, never by substring: "evil-lucashealth.ai" and
// "lucashealth.ai.evil.com" must both fail.
const ALLOWED_EXCHANGE_HOST = "lucashealth.ai";

// Module-memory handoff for the one-time code, mirroring
// clinicalProfileInviteContext.js's staged-context pattern: nothing sensitive is written to
// storage until it has actually been redeemed.
let stagedCode = null;
let stagedServer = null;

// The origin to redeem against, or null when absent/untrusted — in which case the caller falls back
// to the brand-resolved default, which is the pre-existing behaviour.
export function resolveExchangeOrigin(rawUrl) {
  if (!rawUrl) return null;
  try {
    const url = new URL(rawUrl);
    const isLocalDev = url.hostname === "localhost" || url.hostname === "127.0.0.1";
    if (url.protocol !== "https:" && !isLocalDev) return null;
    if (!isLocalDev && url.hostname !== ALLOWED_EXCHANGE_HOST && !url.hostname.endsWith(`.${ALLOWED_EXCHANGE_HOST}`)) return null;
    // Origin only: a path or query smuggled in here would ride along on every URL built from it.
    return url.origin;
  } catch {
    return null;
  }
}

// Run as early as possible (src/index.js, before Sentry.init) so a bearer-shaped one-time code
// never reaches telemetry, a referrer header, or React Router's own history handling. Same
// reasoning as captureClinicalProfileIntakeContext's pre-router URL scrub.
export function captureImpersonationCode(search) {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(search ?? window.location.search);
  const code = params.get(CODE_QUERY_KEY);
  if (!code) {
    // Drop anything staged by an earlier call rather than leaving it for a later one to redeem —
    // the same rule clinicalProfileInviteContext.js follows for its own one-time credentials.
    stagedCode = null;
    stagedServer = null;
    return null;
  }

  stagedCode = code;
  // Validated at redemption, not here: capture's one job is getting the credential out of the URL.
  stagedServer = params.get(SERVER_QUERY_KEY);
  params.delete(CODE_QUERY_KEY);
  params.delete(SERVER_QUERY_KEY);
  const query = params.toString();
  window.history.replaceState(
    window.history.state,
    document.title,
    window.location.pathname + (query ? "?" + query : "") + window.location.hash
  );
  return code;
}

// getBaseURL() reads sessionStorage.brand, which useAuth.js only populates after its first
// render. A fresh impersonation tab has no prior sessionStorage, so resolve it here the same way
// useAuth.js does (detectBrand against the current host/?brand=/stored value) before it's needed.
function ensureBrandResolved() {
  if (sessionStorage.getItem("brand")) return;
  const params = new URLSearchParams(window.location.search);
  const brand = detectBrand(window.location.host, params.get("brand"), sessionStorage.getItem("brand"));
  if (brand) sessionStorage.setItem("brand", brand);
}

// Redeems the code captured by captureImpersonationCode() into a real, tab-scoped support
// session. Await this before the first render (src/index.js) so the app boots already signed in
// instead of flashing the login screen. Returns true only when a session was actually created —
// an expired/already-used code just falls through to the normal login screen.
export async function redeemStagedImpersonationCode() {
  const code = stagedCode;
  const server = stagedServer;
  stagedCode = null;
  stagedServer = null;
  if (!code) return false;

  try {
    ensureBrandResolved();
    // The minting node first: a code exists only in the memory of the server that issued it, and the
    // brand-resolved default is the right backend only when the two happen to coincide. An untrusted
    // or absent value falls back to that default rather than failing the handover outright.
    const trusted = resolveExchangeOrigin(server);
    if (server && !trusted) console.warn(`Ignoring untrusted impersonation exchange host: ${server}`);
    const baseURL = trusted || getBaseURL();
    const res = await fetch(`${baseURL}/web/impersonate-exchange`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      // Without this the browser discards the Set-Cookie: the exchange is cross-origin (this app's
      // host -> the org's backend), and the session cookie IS the point of calling it.
      credentials: "include",
      body: JSON.stringify({ code }),
    });
    if (!res.ok) {
      // Surface WHY. The server answers `{error, error_description}`, and the failures look identical
      // from the outside: a code that expired (60s TTL), one already redeemed (single-use — and a
      // failed attempt burns it too), and one minted on a DIFFERENT ArcadeBrain server than this call
      // reached (codes are held in that server's memory only).
      let detail = "";
      try {
        const body = await res.json();
        detail = body?.error_description || body?.error || "";
      } catch {
        // A non-JSON error body is still worth reporting as its status alone.
      }
      console.warn(
        `Impersonation handover refused (${res.status}${detail ? `: ${detail}` : ""}). ` +
          `Exchanged against ${baseURL} — the code must be redeemed on the same server that minted it.`
      );
      return false;
    }

    const session = await res.json();
    if (!session?.sessionId) return false;

    // Establish the session exactly as login() does, so checkAuth() finds what it expects and nothing
    // downstream can tell this apart from a normal sign-in.
    const hostName = session.hostName || baseURL;
    localStorage.setItem("hostName", hostName);
    // The live axios instance pinned its baseURL at module load, before any of this ran, so writing
    // localStorage alone leaves every later call pointed at the DEFAULT backend while the session
    // cookie sits on this one. login() does exactly this for the same reason (useAuth.js).
    api.api.defaults.baseURL = hostName;
    // Written unconditionally — set from this handover, or CLEARED. A conditional write leaves the
    // previous occupant's value in place: entering org B after org A kept A's `organization`, and the
    // app then asked the new backend for a database it has never heard of. Whoever this tab was
    // before must not survive into whoever it is now.
    setOrClear("brainId", session.currentBrainId);
    setOrClear("organization", session.currentOrganizationName && session.currentOrganizationName.toLowerCase());
    setOrClear("user", session.userEmail);
    setOrClear("authUserId", session.userId);
    // Identity of a DIFFERENT person: nothing about the operator's own last session may be inherited.
    localStorage.removeItem("loginToken");
    localStorage.removeItem("sessionId");
    // sessionStorage, not localStorage: this is what keeps the impersonated session from being picked
    // up by the operator's OTHER tabs on the next checkAutoLogin().
    sessionStorage.setItem("sessionId", session.sessionId);

    // Tab-scoped marker: drives the banner and tells endImpersonation() there is something to end.
    if (session.impersonator) sessionStorage.setItem(IMPERSONATOR_KEY, session.impersonator);
    sessionStorage.setItem(SESSION_KEY, JSON.stringify({ baseURL: hostName }));
    return true;
  } catch (e) {
    console.warn("Impersonation handover failed:", e?.message);
    return false;
  }
}

/** Writes a key from the handover, or removes it — never leaves the previous session's value behind. */
function setOrClear(key, value) {
  if (value) localStorage.setItem(key, value);
  else localStorage.removeItem(key);
}

function readSession() {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function isImpersonating() {
  return !!readSession();
}

export function getImpersonationSession() {
  return readSession();
}

/** The staff email acting as this user, or null in a normal session. Drives the warning banner. */
export function getImpersonator() {
  return sessionStorage.getItem(IMPERSONATOR_KEY) || null;
}

// Ends the support session and hard-reloads.
//
// A hard reload, never a soft state reset: PHI held in React state (an open patient record, a
// half-filled order) must not survive on screen after the operator leaves. And a real logout, not
// just forgetting the cookie — a live session left behind is a way back into the account without a
// second audited handover.
export async function endImpersonation() {
  const session = readSession();
  const baseURL = session?.baseURL || localStorage.getItem("hostName");
  sessionStorage.removeItem(SESSION_KEY);
  sessionStorage.removeItem(IMPERSONATOR_KEY);
  sessionStorage.removeItem("sessionId");
  try {
    if (baseURL) await fetch(`${baseURL}/web/logout`, { method: "POST", credentials: "include" });
  } catch {
    // Best-effort — the reload below still leaves this tab signed out locally.
  }
  window.location.replace("/");
}
